/**
 * Time compression — the original's "skip the boring parts" feature for the
 * long transit legs an 80-mile map forces on a patrol
 * (`docs/game/manual/05-mission-builder.md`'s Mission Builder chapter warns
 * authors that "a flight led by the player won't automatically go to its
 * navigation points if Time Compress is used").
 *
 * ## What's recoverable, and what isn't
 *
 * `RB.EXE`'s own string table carries three verbatim status messages —
 * `TIME COMPRESS IS ON.` / `TIME COMPRESS DEACTIVATED.` /
 * `TIME COMPRESS IS NOT AVAILABLE NOW.` (`docs/game/ui-menus.md`'s in-flight
 * message table, confirmed again here by `strings` on the real binary) —
 * which is genuine evidence the feature exists and is exactly ON/OFF/BLOCKED,
 * not a ladder of speeds: no `"2X"`/`"4X"`/`"8X"` string, or anything
 * numeric next to these three, exists anywhere in the executable.
 * `docs/game/ui-menus.md`'s separate `TIME SCALE` slider is a *different*
 * thing — a Preferences/`F10` frame-rate comfort setting for slow hardware
 * ("nudging the Time Scale slider left for smoother frame rate", the CD's own
 * `INSTALL.TXT`), not this feature.
 *
 * **The key is named after all, and this file used to say it was not.** The
 * Dynamix Quick Reference Card, transcribed in `docs/game/player-bombing.md`
 * §2 after this module was written, gives the original's HOT KEYS row
 * including `C` for time compress. This port binds `K`, because `C` is the
 * camera cycle here (`keys.ts`); that is now a *known divergence* rather than
 * an unavoidable one, and it is recorded as such in the roadmap.
 * `TIME_COMPRESSION_FACTOR` remains a genuine port choice — no build of the
 * game states a factor.
 *
 * ## The one behaviour the strings *do* pin: it can refuse to run
 * "NOT AVAILABLE NOW" only makes sense if some condition can block the feature
 * outright, and a transit-skipping feature has exactly one sim-relevant reason
 * to do that: an **enemy** already close enough to fight. The gate is the AI's
 * own `aiDisengageRangeM` band (~1600 m, `flight-tunables.md`) rather than a
 * new made-up number, and it runs continuously, so a hostile closing inside the
 * band drops compression back to 1x — undocumented in the original, but the
 * only reading that satisfies "compression must never carry you past a fight."
 *
 * The word **enemy** in that paragraph is load-bearing and was not honoured
 * until G44: the gate was handed a side-blind nearest-aeroplane range, which
 * refused compression at spawn on seven catalogue missions because of the
 * player's own formation. `frame-signals.ts`'s `nearestHostileAircraftM` has
 * the census.
 *
 * ## The half that is queued rather than shipped
 * `RB.EXE`'s `YOU NEAR ` block (`THE BOMBERS.` / `THE ZEPPELINS.` /
 * `THE RECON AIRCRAFT.` / `YOUR BALLOONS.`) sits inside this very string run,
 * immediately before `FLIGHT CONTROLS ARE ACTIVE.` — so in the original,
 * arriving at what the mission is *about* is a second thing that ends
 * compression. It is **not** wired here, and the reason is measured rather than
 * lazy: `sim-callouts.ts`'s `isNearingSubject` is a *level* over a 4 km
 * announce radius, and on an escort the player starts 401 m from the machines
 * he is escorting — so using it as a gate makes seven missions permanently
 * uncompressible, which is worse than the gap it closes. What it needs is an
 * *arrival edge*, not a proximity level. Queued with the census; see
 * `autopilot.ts` for the rest of the string map.
 */

import { MAX_CATCHUP_STEPS } from "@red-opus/engine";

/** Port choice — see the module doc. Fast enough to turn a multi-minute transit leg into seconds, without inflating one rendered frame's physics-step count unreasonably. */
export const TIME_COMPRESSION_FACTOR = 8;

/** `MAX_CATCHUP_STEPS` scaled by the same factor, so a compressed frame gets a proportionally larger catch-up budget instead of clamping at the uncompressed 8-step ceiling every single frame. */
export const TIME_COMPRESSION_MAX_STEPS = MAX_CATCHUP_STEPS * TIME_COMPRESSION_FACTOR;

/** The AI's own disengage range (`aiDisengageRangeM`, `flight-tunables.md`) — see the module doc for why this port reuses it as the compression gate instead of a new number. */
export const TIME_COMPRESSION_GATE_RANGE_M = 1600;

export const TIME_COMPRESS_ON_MESSAGE = "TIME COMPRESS IS ON.";
export const TIME_COMPRESS_OFF_MESSAGE = "TIME COMPRESS DEACTIVATED.";
export const TIME_COMPRESS_BLOCKED_MESSAGE = "TIME COMPRESS IS NOT AVAILABLE NOW.";

/**
 * Whether a hostile is close enough to refuse or cancel compression. `null` (no
 * enemy in the sky) never blocks.
 *
 * The caller must pass a **side-aware** range — `frame-signals.ts`'s
 * `nearestHostileAircraftM`, not its side-blind neighbour. See that function
 * for the census, and the module doc above for why the distinction matters.
 */
export function timeCompressionBlocked(nearestHostileM: number | null): boolean {
  return nearestHostileM !== null && nearestHostileM < TIME_COMPRESSION_GATE_RANGE_M;
}

/** `requested` is the player's own last toggle press; `active` is what the sim is actually doing right now — they can disagree for exactly one frame (a blocked request) or until the next reconcile (an auto-drop). */
export interface TimeCompressionState {
  readonly requested: boolean;
  readonly active: boolean;
}

export const IDLE_TIME_COMPRESSION: TimeCompressionState = { requested: false, active: false };

export interface TimeCompressionResult {
  readonly state: TimeCompressionState;
  /** One of the three verbatim RB.EXE strings above, or `null` when nothing changed this frame. */
  readonly message: string | null;
}

/**
 * One frame's worth of the state machine: reconciles what the player last
 * asked for against whether an enemy is currently close enough to block it.
 * Pure — `flight-scene.ts`'s `onFrame` is the only caller, and it is the
 * thing under test here rather than the frame loop itself.
 */
export function reconcileTimeCompression(state: TimeCompressionState, nearestHostileM: number | null): TimeCompressionResult {
  const blocked = timeCompressionBlocked(nearestHostileM);

  if (state.requested && !state.active) {
    return blocked
      ? { state: { requested: false, active: false }, message: TIME_COMPRESS_BLOCKED_MESSAGE }
      : { state: { requested: true, active: true }, message: TIME_COMPRESS_ON_MESSAGE };
  }
  if (state.active && (blocked || !state.requested)) {
    return { state: { requested: false, active: false }, message: TIME_COMPRESS_OFF_MESSAGE };
  }
  return { state, message: null };
}

/** The `advance()` call shape for one frame: the pretend elapsed time and the catch-up ceiling that goes with it. */
export interface TimeCompressionStep {
  readonly frameSeconds: number;
  readonly maxSteps: number;
}

/** `dt` is the real elapsed frame time (already clamped upstream); `active` is this frame's reconciled `TimeCompressionState.active`. Never changes `dt` itself — only how much of it `planFixedSteps` is told to plan for (AD-007a: the physics tick stays fixed-60Hz regardless). */
export function timeCompressionStep(dt: number, active: boolean): TimeCompressionStep {
  return active ? { frameSeconds: dt * TIME_COMPRESSION_FACTOR, maxSteps: TIME_COMPRESSION_MAX_STEPS } : { frameSeconds: dt, maxSteps: MAX_CATCHUP_STEPS };
}


/** The slice of `flight-scene.ts`'s `SceneState` the reconciler reads and writes. */
export interface TimeCompressionHost {
  readonly allowTimeCompression: boolean;
  timeCompression: TimeCompressionState;
}

/** The two callbacks a reconciliation can fire — a message line and the HUD chip signal. */
export interface TimeCompressionCallbacks {
  onMessages(lines: readonly string[]): void;
  onTimeCompression?(active: boolean): void;
}

/**
 * Reconciles time compression against the sky as it stood at the *end of the
 * previous* frame (one 60 Hz frame of latency on the gate, imperceptible) —
 * this frame's own tick hasn't run yet, so there is no fresher answer to
 * "is an enemy close enough to block/cancel it" available before deciding
 * how big a step to plan for it (`time-compression.ts`).
 */
export function reconcileTimeCompressionFor(
  state: TimeCompressionHost,
  nearestM: number | null,
  callbacks: TimeCompressionCallbacks,
): void {
  if (!state.allowTimeCompression) return;
  const reconciled = reconcileTimeCompression(state.timeCompression, nearestM);
  state.timeCompression = reconciled.state;
  if (reconciled.message) {
    callbacks.onMessages([reconciled.message]);
    callbacks.onTimeCompression?.(reconciled.state.active);
  }
}
